import { State } from 'interfaces/state'
import {
  FeedPostComment,
  FeedPostCommentStateProps,
  FeedPostCommentDispatchProps,
  FeedPostCommentOwnProps,
} from 'components/features/socialFeed/FeedPost/FeedPostComment'
import { StoreDispatch } from 'interfaces/component'
import {
  toggleCommentLikeActionCreator,
  toggleAuthorFollowingActionCreator,
  reportCommentActionCreator,
  deleteCommentActionCreator,
  toggleEditCommentActionCreator,
  toggleCommentRepliesActionCreator,
  initCommentReplyActionCreator,
  commentReplyCancelActionCreator,
} from 'actions/feedActions'
import { feedComposeCleanUpActionCreator } from 'actions/feedComposeActions'
import {
  createFeedCommentSelector,
  createCommentAuthorSelector,
  feedPostSelector,
  createHiddenRepliesSelector,
  createHasReplySelector,
} from 'selectors/feedSelectors'
import { connectOptionally, OptionalRendererProps, createOptionalRenderer } from 'tangle-shared'
import { memberFeedInfoSelector } from 'selectors/feedSelectors'
import { createFeedCommentImagesSelector } from 'selectors/feedImagesSelectors'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { getReplyComposeId, getEditCommentComposeId } from 'helpers/composeHelpers'


const mapStateToProps = () => {
  const feedCommentSelector = createFeedCommentSelector()
  const commentAuthorSelector = createCommentAuthorSelector()
  const optionalRenderer = createOptionalRenderer<FeedPostCommentStateProps>()
  const imagesSelector = createFeedCommentImagesSelector()
  const isRepliesHiddenSelector = createHiddenRepliesSelector()
  const hasReplySelector = createHasReplySelector()

  return (state: State, ownProps: FeedPostCommentOwnProps): OptionalRendererProps<FeedPostCommentStateProps> => {
    const {
      likingCommentUids,
      authorFollowingLoadingIds,
      editingCommentsIds,
    } = state.app.feed

    const member = memberFeedInfoSelector(state)
    const author = commentAuthorSelector(state, ownProps)
    const comment = feedCommentSelector(state, ownProps)
    const isRepliesHidden = isRepliesHiddenSelector(state, ownProps)
    const hasReplyCompose = hasReplySelector(state, ownProps)

    if (!author || !comment) {
      return optionalRenderer.fallback()
    }

    const { commentUid } = ownProps
    const post = feedPostSelector(state, { postUid: comment.postUid })

    if (!post) {
      return optionalRenderer.fallback()
    }

    return optionalRenderer.render({
      comment,
      images: imagesSelector(state, ownProps),

      isPostAuthor: 'authorUid' in post ? post.authorUid === comment.authorUid : false,
      authorUid: comment.authorUid,
      authorName: author.username,
      isOwn: !!member && author.uid === member.uid,
      isAuthorFollowed: author.is_followed,
      isAuthorFollowLoading: !!authorFollowingLoadingIds[comment.authorUid],
      isLikeLoading: !!likingCommentUids[commentUid],
      isCommentEditing: !!editingCommentsIds[commentUid],
      isAuthenticated: !!isAuthenticatedSelector(state),
      hasReplies: Boolean(comment.children && comment.children.results.length),
      isRepliesHidden,
      hasReplyCompose,
    })
  }
}

const mapDispatchToProps = (dispatch: StoreDispatch, ownProps: FeedPostCommentOwnProps): FeedPostCommentDispatchProps => ({
  onToggleCommentLike: (isLiked, authorUid) => dispatch(
    toggleCommentLikeActionCreator.request({ toLike: isLiked, commentUid: ownProps.commentUid, authorUid })
  ),
  onToggleFollowAuthor: (toFollow, authorUid) => dispatch(
    toggleAuthorFollowingActionCreator.request({ toFollow, uid: authorUid })
  ),
  onToggleEditComment: () => dispatch(
    toggleEditCommentActionCreator({ isEditing: true, uid: ownProps.commentUid })
  ),
  onCancelEditComment: () => {
    dispatch(
      toggleEditCommentActionCreator({ isEditing: false, uid: ownProps.commentUid })
    )
    dispatch(feedComposeCleanUpActionCreator.request({
      composeId: getEditCommentComposeId(ownProps.commentUid),
    }))
  },
  onSendReport: () => dispatch(
    reportCommentActionCreator.request({ uid: ownProps.commentUid }),
  ),
  onDeleteComment: (comment) => dispatch(
    deleteCommentActionCreator.request({
      uid: comment.uid,
      postUid: comment.postUid,
    }),
  ),
  onReplyComment: () => dispatch(
    initCommentReplyActionCreator({
      commentUid: ownProps.commentUid,
    })
  ),
  onReplyCancel: () => {
    dispatch(commentReplyCancelActionCreator({
      commentUid: ownProps.commentUid,
    }))
    dispatch(feedComposeCleanUpActionCreator.request({
      composeId: getReplyComposeId(ownProps.commentUid),
    }))
  },
  onToggleReplies: (toShow: boolean) => dispatch(
    toggleCommentRepliesActionCreator({
      commentUid: ownProps.commentUid,
      toShow,
    })
  ),
})

export const FeedPostCommentContainer = connectOptionally<
  FeedPostCommentStateProps,
  FeedPostCommentDispatchProps,
  FeedPostCommentOwnProps
>(mapStateToProps, mapDispatchToProps)(FeedPostComment)
